import React, { memo } from "react";
import clsx from "clsx";
import type { V2PrivacyProgress } from "../../privacy";
import {
  PrivateModeProgress,
  privateModeProgressText,
} from "./PrivateModeProgress";
import "../../scss/unlock_private_mode.scss";
import { PrivacyGhost } from "./PrivacyGhost";
// ============================================
// ICON COMPONENTS
// ============================================

const GhostIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 1210 1493" fill="none" {...props}>
    <defs>
      <linearGradient
        id="ghostGradientLogin"
        x1="605"
        y1="0"
        x2="605"
        y2="1493"
        gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#D615EB" stopOpacity={1} />
        <stop offset="100%" stopColor="#8436F5" stopOpacity={0.15} />
      </linearGradient>
    </defs>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M568.97 0.518916C515.968 2.82316 482.805 6.22945 449.241 12.9418C269.897 48.7077 151.57 165.423 81.4365 376.011C41.0593 497.535 13.3057 654.124 3.88742 813.816C-0.921555 896.266 -1.3225 992.447 2.98609 1064.28C7.99554 1150.14 19.0168 1229.59 33.4445 1285.69C47.3711 1339.79 70.7159 1387.77 96.0647 1414.83C129.729 1450.59 166.199 1457.61 209.682 1436.57C228.317 1427.55 240.141 1418.23 272.702 1387.27C308.471 1353.11 325.103 1342.89 346.244 1341.49C356.964 1340.89 365.18 1342.49 375.099 1347.3C391.13 1355.12 402.752 1366.03 431.306 1400.4C454.651 1428.35 464.47 1437.57 488.316 1453.69C516.77 1473.03 545.726 1485.26 575.984 1490.76C591.614 1493.57 620.967 1493.77 635.597 1491.17C666.36 1485.66 693.209 1474.73 720.758 1456.7C747.814 1439.07 766.652 1421.33 788.394 1393.39C804.52 1372.55 822.857 1353.71 831.87 1348.3C849.507 1337.98 868.146 1337.48 888.384 1346.9C903.308 1353.71 914.629 1362.73 943.888 1390.68C965.933 1411.72 976.855 1421.04 988.876 1429.05C1001.2 1437.27 1013.52 1442.98 1025.65 1446.08C1034.46 1448.39 1036.77 1448.59 1049.89 1448.19C1066.62 1447.69 1073.03 1446.08 1086.96 1438.87C1124.23 1419.73 1154.79 1370.14 1175.94 1294.2C1193.37 1231.89 1202.09 1167.57 1208.29 1056.77C1210 1026.21 1210.6 904.786 1209.29 867.82C1198.27 565.861 1137.15 325.017 1035.57 183.255C1011.82 150.295 983.967 120.039 957.516 98.8001C874.558 32.0767 757.026 -0.88366 605.542 0.0180072C589.008 0.118157 572.577 0.318536 568.97 0.518916ZM459.26 340.345C497.634 348.26 525.186 376.011 533.402 414.782C536.007 427.005 537.109 453.453 537.109 503.746C537.109 570.57 534.705 611.645 529.695 627.775C526.99 636.791 521.178 648.112 515.468 655.727C489.919 689.689 438.621 700.609 396.94 681.073C367.384 667.248 350.151 643.604 346.645 611.845C344.44 592.109 343.338 544.02 343.839 494.73C344.741 415.484 346.645 401.458 358.467 383.325C370.089 365.592 389.827 349.763 409.164 342.75C421.688 338.241 443.83 337.139 459.26 340.345ZM790.391 340.045C832.578 347.959 862.132 380.419 867.438 424.501C870.644 450.248 870.644 541.015 867.541 581.59C865.036 614.751 862.434 628.075 856.022 641.501C841.09 672.457 809.834 691.092 772.857 691.092C733.288 691.092 698.515 669.151 684.792 635.489C678.078 618.958 676.773 599.222 676.678 515.768C676.678 418.289 678.38 402.76 691.101 383.325C705.229 361.584 729.175 344.654 752.325 339.944C761.442 338.141 780.479 338.141 790.391 340.045Z"
      fill="url(#ghostGradientLogin)"
    />
  </svg>
));
GhostIcon.displayName = "GhostIcon";

const XmarkIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...props}>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
));
XmarkIcon.displayName = "XmarkIcon";

// ============================================
// MAIN LOGIN MODAL COMPONENT
// ============================================

interface PrivateModeLoginProps {
  /** Reuse the unlock flow when an existing private session has expired. */
  variant?: "default" | "session-expired";
  /** Whether currently signing */
  isSigning?: boolean;
  /** Optional live progress supplied by the privacy provider. */
  progress?: V2PrivacyProgress;
  busyLabel?: string;
  /** Whether login was successful (shows success state briefly before closing) */
  isSuccess?: boolean;
  /** Callback when user clicks unlock */
  onUnlock?: () => void;
  /** Starts the next mobile wallet request from a fresh user action. */
  onContinue?: () => void;
  /** Callback after successful login (called after success animation) */
  onSuccess?: () => void;
  /** Additional class name */
  className?: string;
}

export const PrivateModeLogin: React.FC<PrivateModeLoginProps> = ({
  variant = "default",
  isSigning = false,
  progress,
  busyLabel = "Signing...",
  isSuccess = false,
  onUnlock,
  onContinue,
  onSuccess,
  className,
}) => {
  const awaitingContinuation =
    isSigning && progress?.phase === "wallet-signature-ready" && !!onContinue;
  const sessionExpired = variant === "session-expired";
  // Auto-close after success
  React.useEffect(() => {
    if (isSuccess && onSuccess) {
      const timer = setTimeout(() => {
        onSuccess();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, onSuccess]);

  return (
    <div className={clsx("private-login-modal", className)}>
      {/* Modal Window */}
      <div className="private-login-modal__window">
        {/* Content */}
        <div className="private-login-modal__content">
          {/* Ghost Icon */}
          <div className="private-login-modal__icon">
            <GhostIcon className="private-login-modal__ghost" />
          </div>

          {/* Title */}
          <h2 className="private-login-modal__title">
            {isSuccess
              ? "Logged In"
              : sessionExpired
                ? "Session expired"
                : "Unlock Private Mode"}
          </h2>
          <p className="private-login-modal__subtitle">
            {isSuccess
              ? "Redirecting to Private Mode..."
              : sessionExpired
                ? "Sign in again to continue."
                : "Sign to access your private account"}
          </p>

          {/* Unlock Button */}
          <button
            className={clsx(
              "private-login-modal__button",
              isSigning &&
                !awaitingContinuation &&
                "private-login-modal__button--signing",
              isSuccess && "private-login-modal__button--success"
            )}
            onClick={awaitingContinuation ? onContinue : onUnlock}
            aria-busy={isSigning && !awaitingContinuation}
            disabled={(isSigning && !awaitingContinuation) || isSuccess}>
            {isSuccess ? (
              <>
                {/* The happy ghost plays once (1.5 s, the same time the
                    modal stays open) and rests on its happy face. */}
                <PrivacyGhost
                  motion="success"
                  className="private-login-modal__button-loader filter-white"
                />
                Success
              </>
            ) : isSigning ? (
              <>
                {!awaitingContinuation && (
                  <PrivacyGhost
                    motion="swirl"
                    className="private-login-modal__button-loader filter-white"
                  />
                )}
                <span>
                  {progress
                    ? privateModeProgressText(progress).label
                    : busyLabel}
                </span>
              </>
            ) : sessionExpired ? (
              "Sign in again"
            ) : (
              "Sign to Unlock"
            )}
          </button>
          {isSigning && progress && <PrivateModeProgress progress={progress} />}
        </div>
      </div>
    </div>
  );
};

export default PrivateModeLogin;
